import type { CalendarDate, Time } from "@internationalized/date";
import { DateTime } from "luxon";
import type { UseFormReturn } from "react-hook-form";
import type { WizardParkingChoice } from "../../../../PermitContextProvider/types";

const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const MINUTES_PER_DAY = MINUTES_PER_HOUR * HOURS_PER_DAY;

type IntervalFormData = {
	intervals: {
		startDate: CalendarDate;
		endDate: CalendarDate;
		startTime: Time;
		endTime: Time;
		durationInMinutes?: number;
	}[];
};

export const computeMaxDurationMinutes = (
	wizardSettings: WizardParkingChoice["time"],
	startDateTime: DateTime | undefined,
): number | undefined => {
	const caps: number[] = [];

	if (wizardSettings.duration.max) {
		caps.push(wizardSettings.duration.max);
	}

	// Combine with endTime.max (from ExpiryTimeBeforeTimeRule) so same-day expiry caps the
	// duration — e.g. start 8 PM + expiry-before 23:59 → max ~3h 59m, not the full 12 h.
	if (startDateTime && wizardSettings.endTime.max) {
		const [h, m, s] = wizardSettings.endTime.max.split(":").map(Number);
		const endCap = startDateTime.set({
			hour: h ?? 0,
			minute: m ?? 0,
			second: s ?? 0,
			millisecond: 0,
		});
		const minutesToEndCap = Math.floor(endCap.diff(startDateTime, "minutes").minutes);
		if (minutesToEndCap > 0) {
			caps.push(minutesToEndCap);
		}
	}

	if (wizardSettings.duration.days !== undefined && startDateTime) {
		const endOfDay = startDateTime.endOf("day");
		const remainingMinutesInDay = Math.floor(endOfDay.diff(startDateTime, "minutes").minutes);
		caps.push(wizardSettings.duration.days * MINUTES_PER_DAY + remainingMinutesInDay);
	}

	return caps.length > 0 ? Math.min(...caps) : undefined;
};

export const useDurationCalculations = (
	wizardSettings: WizardParkingChoice["time"],
	methods: UseFormReturn<IntervalFormData>,
	timezone: string,
) => {
	const calculateMaxDurationMinutes = (index: number): number | undefined => {
		const startDate = methods.watch(`intervals.${index}.startDate`);
		const startTime = methods.watch(`intervals.${index}.startTime`);
		const startDateTime =
			startDate && startTime
				? DateTime.fromFormat(`${startDate.toString()} ${startTime.toString()}`, "yyyy-MM-dd HH:mm:ss", {
						zone: timezone,
					})
				: undefined;

		return computeMaxDurationMinutes(wizardSettings, startDateTime);
	};

	const calculateMinDurationMinutes = (index: number): number | undefined => {
		if (wizardSettings.duration.min) {
			return wizardSettings.duration.min;
		}

		const startDate = methods.watch(`intervals.${index}.startDate`);
		const startTime = methods.watch(`intervals.${index}.startTime`);

		if (!startDate || !startTime) {
			return undefined;
		}

		const startDateTime = DateTime.fromFormat(
			`${startDate.toString()} ${startTime.toString()}`,
			"yyyy-MM-dd HH:mm:ss",
			{ zone: timezone },
		);

		if (wizardSettings.duration.days !== undefined) {
			if (wizardSettings.duration.days === 0) {
				return undefined;
			}

			if (wizardSettings.endTime.min) {
				const targetDate = startDateTime.plus({ days: wizardSettings.duration.days }).startOf("day");
				const [hours, minutes] = wizardSettings.endTime.min.split(":");
				const targetDateTime = targetDate.set({
					hour: Number.parseInt(hours!),
					minute: Number.parseInt(minutes!),
				});
				return Math.floor(targetDateTime.diff(startDateTime, "minutes").minutes);
			}

			const startOfDay = startDateTime.startOf("day");
			const minutesToStartOfDay = Math.floor(startOfDay.diff(startDateTime, "minutes").minutes);
			return wizardSettings.duration.days * MINUTES_PER_DAY + minutesToStartOfDay;
		}

		if (wizardSettings.endDate.min) {
			const endTime = wizardSettings.endTime.min ?? "00:00:00";
			const minEndDateTime = DateTime.fromFormat(
				`${wizardSettings.endDate.min} ${endTime}`,
				"yyyy-MM-dd HH:mm:ss",
				{ zone: timezone },
			);

			const diffMinutes = Math.floor(minEndDateTime.diff(startDateTime, "minutes").minutes);
			return diffMinutes > 0 ? diffMinutes : undefined;
		}

		return undefined;
	};

	return { calculateMaxDurationMinutes, calculateMinDurationMinutes };
};
